import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import BestSellerCard from "@/components/BestSellerCard";

const STORY_IMG = "/images/story-night-soft-life.webp";

const BEST_SELLER_COPY = {
  "soft-life-reset-workbook": {
    name: "Soft Life Reset Workbook",
    description:
      "A guided journal to release the hustle and reconnect with what you truly want. Thoughtful prompts for mindset, money, and self-worth. Reset your inner world, one beautiful page at a time.",
    price: 19,
  },
  "soft-financial-calendar": {
    name: "365 Day Soft Life Reset Calendar",
    description:
      "Design your days with intention and ease. A luxe 365 day calendar with space for goals, gentle routines, and financial check-ins. Structure that still feels soft.",
    price: 37,
  },
};

const Home = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const featured = products
    .filter((p) => BEST_SELLER_COPY[p.id])
    .map((p) => ({ display: { ...p, ...BEST_SELLER_COPY[p.id] }, cart: p }));

  return (
    <div data-testid="home-page" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center min-h-[88vh] py-16">
          <div className="fade-up">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C6A769] mb-6">
              Digital Products for the Ambitious
            </p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[#4A3F35]">
              Luxury Is the<br />Lifestyle.<br />
              <span className="italic text-[#C6A769]">Wealth</span> Is the Goal.
            </h1>
            <p className="mt-8 text-base text-[#8A7D71] max-w-md leading-relaxed">
              Beautifully crafted e-books, templates and courses to help you build
              the elegant, abundant life you keep imagining — delivered instantly.
            </p>
            <div className="mt-10 flex items-center gap-7">
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="group inline-flex items-center gap-3 bg-[#4A3F35] text-[#FAF7F2] px-9 py-4 rounded-sm text-sm uppercase tracking-[0.15em] hover:bg-[#C6A769] transition-colors duration-500"
              >
                Shop the Collection
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
          <div className="relative aspect-square fade-up" style={{ animationDelay: "150ms" }}>
            <div className="absolute inset-0 overflow-hidden rounded-sm bg-[#2b2520]">
              <img
                src="/images/hero-soft-life.webp"
                alt="Luxury soft life lifestyle scene featuring an elegant Black woman relaxing with coffee beside the Soft Life Reset Workbook, 365 Soft Life Calendar, and Soft Life Principles."
                fetchPriority="high"
                data-testid="hero-image"
                className="w-full h-full object-cover object-[center_18%]"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#E8CFCB] px-8 py-6 rounded-sm hidden sm:block">
              <p className="font-serif-display text-3xl text-[#4A3F35] leading-none">3</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A3F35]/70 mt-1">Premium Products</p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="border-y border-[#4A3F35]/10 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid sm:grid-cols-3 gap-8">
          {[
            { icon: Download, title: "Instant Delivery", desc: "Download links the moment you pay." },
            { icon: ShieldCheck, title: "Secure Checkout", desc: "Payments protected by Stripe." },
            { icon: Sparkles, title: "Crafted with Care", desc: "Editorial design in every file." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-4" data-testid={`value-prop-${i}`}>
              <f.icon className="w-6 h-6 text-[#C6A769] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-serif-display text-lg text-[#4A3F35]">{f.title}</p>
                <p className="text-sm text-[#8A7D71]">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C6A769] mb-3">The Edit</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#4A3F35]">Best Sellers</h2>
          </div>
          <Link
            to="/shop"
            data-testid="featured-view-all"
            className="text-sm uppercase tracking-[0.15em] text-[#4A3F35] border-b border-[#4A3F35]/40 pb-1 hover:text-[#C6A769] hover:border-[#C6A769] transition-colors duration-300 whitespace-nowrap"
          >
            View All
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-8 lg:gap-10">
          {featured.map((p, i) => (
            <BestSellerCard key={p.cart.id} product={p.display} cartProduct={p.cart} index={i} />
          ))}
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="bg-[#E8CFCB]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative h-[60vh] rounded-sm overflow-hidden order-2 lg:order-1">
            <img src={STORY_IMG} alt="Luxury nighttime soft life scene featuring the Soft Life Reset Workbook, 365 Soft Life Calendar, Soft Life Principles, BENZ branding, candlelight, and city skyline." loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C6A769] mb-6">The Philosophy</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#4A3F35] leading-tight">
              Wealth begins as a decision, then becomes a lifestyle.
            </h2>
            <p className="mt-7 text-base text-[#8A7D71] leading-relaxed max-w-lg">
              BenztheBrand exists for the woman who refuses to wait for permission.
              Every product is designed to make the pursuit of more — more freedom,
              more elegance, more wealth — feel beautiful and entirely possible.
            </p>
            <Link
              to="/shop"
              data-testid="story-cta"
              className="mt-9 inline-flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-[#4A3F35] border-b border-[#4A3F35]/40 pb-1 hover:text-[#C6A769] hover:border-[#C6A769] transition-colors duration-300"
            >
              Begin Your Journey <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
